import React, { useState, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { FontFamily } from '@tiptap/extension-font-family';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import '../../styles/ArticleEditor.css';
import '../../styles/Blog.css';
import { FaImage, FaVideo, FaGripVertical, FaTrash, FaTimes, FaEye, FaEdit, FaPlus, FaBold, FaItalic, FaUnderline, FaStrikethrough, FaListUl, FaListOl, FaAlignLeft, FaAlignCenter, FaAlignRight, FaAlignJustify, FaLink, FaUnlink, FaUndo, FaRedo } from 'react-icons/fa';
import config from '../../constants/config';
import axiosPost from '../../helpers/axios-helper';

// Helper function to convert Draft.js content to HTML for TipTap
const draftToHtml = (draftContent) => {
  try {
    if (!draftContent) return '';
    
    let parsed = draftContent;
    if (typeof draftContent === 'string') {
      parsed = JSON.parse(draftContent);
    }
    
    if (!parsed.blocks || !Array.isArray(parsed.blocks)) return '';
    
    let html = '';
    parsed.blocks.forEach(block => {
      const text = block.text || '';
      let styledText = text;
      
      // Apply inline styles
      if (block.inlineStyleRanges && block.inlineStyleRanges.length > 0) {
        const segments = [];
        let lastOffset = 0;
        
        block.inlineStyleRanges.forEach(style => {
          if (style.offset > lastOffset) {
            segments.push({ text: text.substring(lastOffset, style.offset), styles: [] });
          }
          segments.push({
            text: text.substring(style.offset, style.offset + style.length),
            styles: [style.style]
          });
          lastOffset = style.offset + style.length;
        });
        
        if (lastOffset < text.length) {
          segments.push({ text: text.substring(lastOffset), styles: [] });
        }
        
        styledText = segments.map(seg => {
          let result = seg.text;
          if (seg.styles.includes('BOLD')) result = `<strong>${result}</strong>`;
          if (seg.styles.includes('ITALIC')) result = `<em>${result}</em>`;
          if (seg.styles.includes('UNDERLINE')) result = `<u>${result}</u>`;
          if (seg.styles.includes('STRIKETHROUGH')) result = `<s>${result}</s>`;
          return result;
        }).join('');
      }
      
      // Apply block type
      switch (block.type) {
        case 'header-one':
          html += `<h1>${styledText}</h1>`;
          break;
        case 'header-two':
          html += `<h2>${styledText}</h2>`;
          break;
        case 'header-three':
          html += `<h3>${styledText}</h3>`;
          break;
        case 'header-four':
          html += `<h4>${styledText}</h4>`;
          break;
        case 'header-five':
          html += `<h5>${styledText}</h5>`;
          break;
        case 'header-six':
          html += `<h6>${styledText}</h6>`;
          break;
        case 'blockquote':
          html += `<blockquote>${styledText}</blockquote>`;
          break;
        case 'unordered-list-item':
          html += `<ul><li>${styledText}</li></ul>`;
          break;
        case 'ordered-list-item':
          html += `<ol><li>${styledText}</li></ol>`;
          break;
        default:
          html += `<p>${styledText || '<br>'}</p>`;
      }
    });
    
    return html;
  } catch (e) {
    console.error('Error converting Draft.js to HTML:', e);
    return '';
  }
};

// Helper function to convert HTML to Draft.js format for backend compatibility
const htmlToDraft = (html) => {
  if (!html) {
    return JSON.stringify({ blocks: [{ key: 'init', text: '', type: 'unstyled', depth: 0, inlineStyleRanges: [], entityRanges: [], data: {} }], entityMap: {} });
  }
  
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  const blocks = [];
  let keyCounter = 0;
  
  const processNode = (node) => {
    const tagName = node.tagName ? node.tagName.toLowerCase() : '';
    const text = node.textContent || '';
    
    let blockType = 'unstyled';
    if (tagName === 'h1') blockType = 'header-one';
    else if (tagName === 'h2') blockType = 'header-two';
    else if (tagName === 'h3') blockType = 'header-three';
    else if (tagName === 'h4') blockType = 'header-four';
    else if (tagName === 'h5') blockType = 'header-five';
    else if (tagName === 'h6') blockType = 'header-six';
    else if (tagName === 'blockquote') blockType = 'blockquote';
    else if (tagName === 'li') {
      const parent = node.parentElement;
      blockType = parent && parent.tagName.toLowerCase() === 'ol' ? 'ordered-list-item' : 'unordered-list-item';
    }
    
    const inlineStyleRanges = [];
    const findStyles = (n, offset = 0) => {
      if (n.nodeType === Node.TEXT_NODE) {
        return n.textContent.length;
      }
      
      let length = 0;
      Array.from(n.childNodes).forEach(child => {
        const childTag = child.tagName ? child.tagName.toLowerCase() : '';
        const childLength = child.textContent.length;
        
        if (childTag === 'strong' || childTag === 'b') {
          inlineStyleRanges.push({ offset: offset + length, length: childLength, style: 'BOLD' });
        }
        if (childTag === 'em' || childTag === 'i') {
          inlineStyleRanges.push({ offset: offset + length, length: childLength, style: 'ITALIC' });
        }
        if (childTag === 'u') {
          inlineStyleRanges.push({ offset: offset + length, length: childLength, style: 'UNDERLINE' });
        }
        if (childTag === 's' || childTag === 'strike') {
          inlineStyleRanges.push({ offset: offset + length, length: childLength, style: 'STRIKETHROUGH' });
        }
        
        length += findStyles(child, offset + length);
      });
      
      return length;
    };
    
    findStyles(node);
    
    blocks.push({
      key: `block-${keyCounter++}`,
      text,
      type: blockType,
      depth: 0,
      inlineStyleRanges,
      entityRanges: [],
      data: {}
    });
  };
  
  Array.from(tempDiv.children).forEach(processNode);
  
  if (blocks.length === 0) {
    blocks.push({ key: 'init', text: '', type: 'unstyled', depth: 0, inlineStyleRanges: [], entityRanges: [], data: {} });
  }
  
  return JSON.stringify({ blocks, entityMap: {} });
};

// TipTap Editor Component
const TipTapEditor = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
      }),
      TextStyle,
      Color,
      FontFamily,
    ],
    content: content || '<p></p>',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '<p></p>');
    }
  }, [content]);

  if (!editor) {
    return null;
  }

  const setLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="tiptap-editor-wrapper">
      <div className="tiptap-toolbar">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'is-active' : ''}
          title="Bold"
        >
          <FaBold />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
          title="Italic"
        >
          <FaItalic />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editor.isActive('underline') ? 'is-active' : ''}
          title="Underline"
        >
          <FaUnderline />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={editor.isActive('strike') ? 'is-active' : ''}
          title="Strikethrough"
        >
          <FaStrikethrough />
        </button>
        <div className="toolbar-divider"></div>
        <select
          onChange={(e) => {
            const value = e.target.value;
            if (value === 'p') {
              editor.chain().focus().setParagraph().run();
            } else {
              editor.chain().focus().toggleHeading({ level: parseInt(value) }).run();
            }
          }}
          value={
            editor.isActive('heading', { level: 1 }) ? '1' :
            editor.isActive('heading', { level: 2 }) ? '2' :
            editor.isActive('heading', { level: 3 }) ? '3' :
            editor.isActive('heading', { level: 4 }) ? '4' :
            editor.isActive('heading', { level: 5 }) ? '5' :
            editor.isActive('heading', { level: 6 }) ? '6' : 'p'
          }
        >
          <option value="p">Normal</option>
          <option value="1">H1</option>
          <option value="2">H2</option>
          <option value="3">H3</option>
          <option value="4">H4</option>
          <option value="5">H5</option>
          <option value="6">H6</option>
        </select>
        <div className="toolbar-divider"></div>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          title="Bullet List"
        >
          <FaListUl />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
          title="Numbered List"
        >
          <FaListOl />
        </button>
        <div className="toolbar-divider"></div>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
          title="Align Left"
        >
          <FaAlignLeft />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
          title="Align Center"
        >
          <FaAlignCenter />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
          title="Align Right"
        >
          <FaAlignRight />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          className={editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}
          title="Justify"
        >
          <FaAlignJustify />
        </button>
        <div className="toolbar-divider"></div>
        <button
          type="button"
          onClick={setLink}
          className={editor.isActive('link') ? 'is-active' : ''}
          title="Add Link"
        >
          <FaLink />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().unsetLink().run()}
          disabled={!editor.isActive('link')}
          title="Remove Link"
        >
          <FaUnlink />
        </button>
        <div className="toolbar-divider"></div>
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
        >
          <FaUndo />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
        >
          <FaRedo />
        </button>
      </div>
      <EditorContent editor={editor} className="tiptap-editor-content" />
    </div>
  );
};

// Component for draggable content blocks
const ContentBlock = ({ id, type, content, index, moveBlock, removeBlock, updateBlockContent }) => {
  const ref = useRef(null);
  
  const [{ isDragging }, drag] = useDrag({
    type: 'BLOCK',
    item: { id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  
  const [, drop] = useDrop({
    accept: 'BLOCK',
    hover: (item, monitor) => {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      
      // Move the block
      moveBlock(dragIndex, hoverIndex);
      
      // Update the index for the dragged item
      item.index = hoverIndex;
    },
  });
  
  drag(drop(ref));

  return (
    <div 
      ref={ref} 
      className={`content-block ${type} ${isDragging ? 'dragging' : ''}`}
    >
      <div className="block-header">
        <div className="drag-handle">
          <FaGripVertical />
        </div>
        <div className="block-type">{type.charAt(0).toUpperCase() + type.slice(1)}</div>
        <button className="remove-block" onClick={() => removeBlock(id)}>
          <FaTrash />
        </button>
      </div>
      <div className="block-content">
        {type === 'text' && (
          <div className="editor-container">
            <TipTapEditor
              content={typeof content === 'string' ? (content.startsWith('{') ? draftToHtml(content) : content) : draftToHtml(content)}
              onChange={(html) => updateBlockContent(id, html)}
            />
          </div>
        )}
        {type === 'image' && (
          <div className="media-block">
            {content.images && content.images.length > 0 ? (
              <>
                <div className="image-gallery">
                  {content.images.map((image, idx) => (
                    <div key={idx} className="gallery-item">
                      <img src={image.url} alt={image.caption || `Image ${idx + 1}`} />
                      <div className="gallery-item-actions">
                        <button 
                          className="remove-image-btn" 
                          onClick={() => {
                            const updatedImages = content.images.filter((_, i) => i !== idx);
                            updateBlockContent(id, { ...content, images: updatedImages });
                          }}
                        >
                          <FaTrash />
                        </button>
                      </div>
                      <input 
                        type="text" 
                        placeholder={`Caption for image ${idx + 1}`} 
                        value={image.caption || ''} 
                        onChange={(e) => {
                          const updatedImages = [...content.images];
                          updatedImages[idx] = { ...updatedImages[idx], caption: e.target.value };
                          updateBlockContent(id, { ...content, images: updatedImages });
                        }}
                        className="media-caption"
                      />
                    </div>
                  ))}
                </div>
                <div className="add-more-images">
                  <button 
                    className="add-image-btn" 
                    onClick={(e) => {
                      e.preventDefault(); // Prevent default behavior
                      e.stopPropagation(); // Stop event propagation
                      
                      // Use a hidden file input that's already in the DOM
                      const fileInputId = `file-input-${id}`;
                      let fileInput = document.getElementById(fileInputId);
                      
                      // If it doesn't exist, create it and add it to the DOM
                      if (!fileInput) {
                        fileInput = document.createElement('input');
                        fileInput.type = 'file';
                        fileInput.accept = 'image/*';
                        fileInput.id = fileInputId;
                        fileInput.style.display = 'none';
                        document.body.appendChild(fileInput);
                        
                        // Add the change event listener
                        fileInput.addEventListener('change', (event) => {
                          if (event.target.files && event.target.files[0]) {
                            const reader = new FileReader();
                            reader.onload = (readerEvent) => {
                              const newImage = { url: readerEvent.target.result, caption: '' };
                              updateBlockContent(id, { 
                                ...content, 
                                images: [...content.images, newImage] 
                              });
                            };
                            reader.readAsDataURL(event.target.files[0]);
                          }
                        });
                      }
                      
                      // Trigger the file input click
                      fileInput.click();
                    }}
                  >
                    <FaPlus /> Add Another Image
                  </button>
                  <input 
                    type="text" 
                    placeholder="Or paste image URL here" 
                    className="add-image-url" 
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value) {
                        const newImage = { url: e.target.value, caption: '' };
                        updateBlockContent(id, { 
                          ...content, 
                          images: [...content.images, newImage] 
                        });
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="media-upload">
                <input 
                  type="text" 
                  placeholder="Image URL" 
                  onChange={(e) => {
                    if (e.target.value) {
                      updateBlockContent(id, { 
                        ...content, 
                        images: [{ url: e.target.value, caption: '' }] 
                      });
                    }
                  }}
                  className="media-url-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value) {
                      updateBlockContent(id, { 
                        ...content, 
                        images: [{ url: e.target.value, caption: '' }] 
                      });
                    }
                  }}
                />
                <span className="or-divider">OR</span>
                <label className="upload-button">
                  Upload Image
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          updateBlockContent(id, { 
                            ...content, 
                            images: [{ url: event.target.result, caption: '' }] 
                          });
                        };
                        reader.readAsDataURL(e.target.files[0]);
                      }
                    }}
                  />
                </label>
              </div>
            )}
          </div>
        )}
        {type === 'video' && (
          <div className="media-block">
            {content.url ? (
              <>
                <div className="video-container">
                  {(content.url.startsWith('data:') || content.url.startsWith('blob:')) ? (
                    <video 
                      src={content.url} 
                      title={content.caption || 'Article video'}
                      controls
                      className="uploaded-video"
                    ></video>
                  ) : (
                    <iframe 
                      src={formatVideoUrl(content.url)} 
                      title={content.caption || 'Article video'}
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen
                    ></iframe>
                  )}
                </div>
                <input 
                  type="text" 
                  placeholder="Caption" 
                  value={content.caption || ''} 
                  onChange={(e) => updateBlockContent(id, { ...content, caption: e.target.value })}
                  className="media-caption"
                />
                <button 
                  className="change-media" 
                  onClick={() => updateBlockContent(id, { ...content, url: '' })}
                >
                  Change Video
                </button>
              </>
            ) : (
              <div className="media-upload">
                <input 
                  type="text" 
                  placeholder="Video URL (YouTube, Vimeo, etc.)" 
                  onChange={(e) => {
                    const inputUrl = e.target.value;
                    updateBlockContent(id, { ...content, url: inputUrl });
                  }}
                  className="media-url-input"
                />
                <span className="or-divider">OR</span>
                <label className="upload-button">
                  Upload Video
                  <input 
                    type="file" 
                    accept="video/*" 
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          updateBlockContent(id, { ...content, url: event.target.result });
                        };
                        reader.readAsDataURL(e.target.files[0]);
                      }
                    }}
                  />
                </label>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to format video URLs for embedding
const formatVideoUrl = (url) => {
  if (!url) return '';
  
  // Handle YouTube URLs
  if (url.includes('youtube.com/watch')) {
    const videoId = new URL(url).searchParams.get('v');
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
  }
  
  // Handle YouTube short URLs
  if (url.includes('youtu.be')) {
    const videoId = url.split('/').pop().split('?')[0];
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
  }
  
  // Handle Vimeo URLs
  if (url.includes('vimeo.com')) {
    const videoId = url.split('/').pop().split('?')[0];
    if (videoId) {
      return `https://player.vimeo.com/video/${videoId}`;
    }
  }
  
  // Return the original URL if no specific format is needed
  return url;
};

// Article Preview Component — matches Blog.js layout/styles exactly
const ArticlePreview = ({ articleData }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const closeLightbox = () => setLightboxOpen(false);
  const showPrev = () => setLightboxIndex((i) => (i - 1 + lightboxImages.length) % lightboxImages.length);
  const showNext = () => setLightboxIndex((i) => (i + 1) % lightboxImages.length);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, lightboxImages.length]);

  // Align date formatting with Blog.js (expects unix seconds)
  const formatDate = (unix) => {
    if (!unix) return '';
    try { return new Date(unix * 1000).toLocaleDateString(); } catch { return ''; }
  };

  const { title, excerpt, image, category, tags, blocks, date_published, author } = articleData || {};
  const hero = articleData?.featured_image_url || image;

  const renderBlocks = (blocksArr) => {
    if (!Array.isArray(blocksArr)) return null;
    return blocksArr.map((b, idx) => {
      if (b.type === 'header') {
        return (
          <h2 key={idx} className="blog-block-header">
            {b.content}
          </h2>
        );
      }
      if (b.type === 'image') {
        const c = b.content;
        if (typeof c === 'string') {
          return (
            <div key={idx} className="blog-image-item">
              <img
                src={c}
                alt=""
                className="blog-block-image blog-image-clickable"
                onClick={() => {
                  setLightboxImages([c]);
                  setLightboxIndex(0);
                  setLightboxOpen(true);
                }}
              />
            </div>
          );
        }
        if (c && Array.isArray(c.images)) {
          const images = c.images;
          const Wrapper = ({ children }) => (
            <div className={images.length > 1 ? 'blog-images-grid' : ''}>{children}</div>
          );
          return (
            <Wrapper key={idx}>
              {images.map((im, i) => (
                <div key={`img-${idx}-${i}`} className="blog-image-item">
                  <img
                    src={im.url}
                    alt={im.caption || ''}
                    className="blog-block-image blog-image-clickable"
                    onClick={() => {
                      const urls = images.map(m => m.url);
                      setLightboxImages(urls);
                      setLightboxIndex(i);
                      setLightboxOpen(true);
                    }}
                  />
                  {im.caption && (
                    <div className="blog-image-caption">{im.caption}</div>
                  )}
                </div>
              ))}
            </Wrapper>
          );
        }
        return null;
      }
      if (b.type === 'text') {
        const c = b.content;
        try {
          // If content is HTML string (from TipTap), render it directly
          if (typeof c === 'string') {
            if (c.startsWith('<') || c.includes('<p>') || c.includes('<h')) {
              // It's HTML content from TipTap
              return (
                <div 
                  key={idx} 
                  className="blog-block-html"
                  dangerouslySetInnerHTML={{ __html: c }}
                />
              );
            }
            // Try to parse as Draft.js JSON for backward compatibility
            if (c.startsWith('{')) {
              const raw = JSON.parse(c);
              if (raw && Array.isArray(raw.blocks)) {
                return (
                  <div key={idx}>
                    {raw.blocks.map((blk, bi) => (
                      <p key={`p-${idx}-${bi}`} className="blog-block-paragraph">
                        {blk.text}
                      </p>
                    ))}
                  </div>
                );
              }
            }
            // Plain text
            return (
              <p key={idx} className="blog-block-paragraph">
                {c}
              </p>
            );
          }
          // Raw DraftJS object for backward compatibility
          if (c && typeof c === 'object' && (c.blocks || c.entityMap !== undefined)) {
            const raw = c;
            return (
              <div key={idx}>
                {raw.blocks.map((blk, bi) => (
                  <p key={`p-${idx}-${bi}`} className="blog-block-paragraph">
                    {blk.text}
                  </p>
                ))}
              </div>
            );
          }
        } catch (_) { /* fall through */ }
        return (
          <p key={idx} className="blog-block-paragraph">
            {typeof c === 'string' ? c : ''}
          </p>
        );
      }
      return null;
    });
  };

  return (
    <div className="blog-container">
      {hero && (
        <img
          src={hero}
          alt={title}
          className="blog-hero"
        />
      )}

      <h1 className="blog-title">{title}</h1>

      <div className="blog-meta">
        {category && <span className="blog-category">{category}</span>}
        {author && <span style={{ marginRight: '15px' }}>By {author}</span>}
        {(author || date_published) && <span className="blog-meta-sep">|</span>}
        {date_published && <span>{formatDate(date_published)}</span>}
      </div>

      {excerpt && (
        <div className="blog-excerpt">{excerpt}</div>
      )}

      <div className="blog-blocks">
        {renderBlocks(blocks)}
      </div>

      {Array.isArray(tags) && tags.length > 0 && (
        <div style={{ marginTop: '30px' }}>
          <div style={{ fontWeight: 600, marginBottom: '5px', fontFamily: 'Lato, sans-serif', letterSpacing: '1px' }}>Tags:</div>
          <div>
            {tags.map((tag, i) => (
              <span key={`${tag}-${i}`} style={{ display: 'inline-block', background: '#e0e0e0', padding: '3px 8px', borderRadius: '4px', margin: '0 5px 5px 0', fontSize: '14px', fontFamily: 'Lato, sans-serif', letterSpacing: '1px' }}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Author block (preview) */}
      {author && (typeof author === 'object' ? (author.name || author.image || author.subtitle) : author) && (
        <div className="blog-author">
          {typeof author === 'object' && author.image ? (
            <img src={author.image} alt={author.name || 'Author'} className="blog-author-avatar" />
          ) : null}
          <div className="blog-author-meta">
            <div className="blog-author-name">{typeof author === 'object' ? (author.name || '') : String(author)}</div>
            {typeof author === 'object' && author.subtitle ? (
              <div className="blog-author-subtitle">{author.subtitle}</div>
            ) : null}
          </div>
        </div>
      )}

      {lightboxOpen && lightboxImages.length > 0 && (
        <div className="lightbox-backdrop" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">×</button>
            {lightboxImages.length > 1 && (
              <>
                <button className="lightbox-nav lightbox-prev" onClick={showPrev} aria-label="Previous">‹</button>
                <button className="lightbox-nav lightbox-next" onClick={showNext} aria-label="Next">›</button>
              </>
            )}
            <img src={lightboxImages[lightboxIndex]} alt="" className="lightbox-image" />
          </div>
        </div>
      )}
    </div>
  );
};

// Main Article Editor Component
const ArticleEditor = ({ initialData, onSave, onClose, mode }) => {
  // mode can be 'article' (default), 'queer-landscape', 'site-page', or 'travel-guide'
  const isQueerLandscape = mode === 'queer-landscape';
  const isTravelGuide = mode === 'travel-guide';
  const isSitePage = mode === 'site-page';
  const useParentSave = isQueerLandscape || isSitePage || isTravelGuide;
  const [title, setTitle] = useState(initialData?.title || '');
  const [excerpt, setExcerpt] = useState(initialData?.excerpt || '');
  const [featuredImage, setFeaturedImage] = useState(initialData?.image || '');
  const [category, setCategory] = useState(initialData?.category || 'Travel');
  const [tags, setTags] = useState(initialData?.tags?.join(', ') || '');
  // Countries data and selection
  const [countries, setCountries] = useState([]);
  const [countryId, setCountryId] = useState(initialData?.country_id || '');
  const [status, setStatus] = useState(initialData?.status || 'draft');
  // Map incoming blocks - convert Draft.js to HTML for TipTap
  const mapBlocks = (srcBlocks) => {
    if (!Array.isArray(srcBlocks)) return null;
    return srcBlocks.map(b => {
      if (b.type === 'text') {
        try {
          // Convert Draft.js content to HTML
          if (typeof b.content === 'string') {
            if (b.content.startsWith('{')) {
              // It's Draft.js JSON
              return { ...b, content: draftToHtml(b.content) };
            }
            // It's already HTML or plain text
            return { ...b, content: b.content || '<p></p>' };
          }
          if (typeof b.content === 'object' && b.content) {
            // It's a Draft.js object
            return { ...b, content: draftToHtml(JSON.stringify(b.content)) };
          }
          return { ...b, content: '<p></p>' };
        } catch (e) {
          console.error('Error mapping block:', e);
          return { ...b, content: '<p></p>' };
        }
      }
      return b;
    });
  };
  const initialMappedBlocks = mapBlocks(initialData?.blocks);
  const [blocks, setBlocks] = useState(initialMappedBlocks && initialMappedBlocks.length ? initialMappedBlocks : [
    {
      id: 'block-1',
      type: 'text',
      content: '<p></p>',
    }
  ]);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  // Author fields
  const [authorName, setAuthorName] = useState(
    (initialData?.author && typeof initialData.author === 'object' ? initialData.author.name : initialData?.author_name) || ''
  );
  const [authorSubtitle, setAuthorSubtitle] = useState(
    (initialData?.author && typeof initialData.author === 'object' ? initialData.author.subtitle : initialData?.author_subtitle) || ''
  );
  const [authorImage, setAuthorImage] = useState(
    (initialData?.author && typeof initialData.author === 'object' ? initialData.author.image : initialData?.author_image) || ''
  );
  const [authorLocationFrom, setAuthorLocationFrom] = useState(
    (initialData?.author && typeof initialData.author === 'object' ? initialData.author.location_from : initialData?.author_location_from) || ''
  );
  const [authorLocationCurrent, setAuthorLocationCurrent] = useState(
    (initialData?.author && typeof initialData.author === 'object' ? initialData.author.location_current : initialData?.author_location_current) || ''
  );
  const [authorTravellerType, setAuthorTravellerType] = useState(
    (initialData?.author && typeof initialData.author === 'object' ? initialData.author.traveller_type : initialData?.author_traveller_type) || ''
  );
  const [authorTwitterUrl, setAuthorTwitterUrl] = useState(
    (initialData?.author && typeof initialData.author === 'object' ? initialData.author.twitter_url : initialData?.author_twitter_url) || ''
  );
  const [authorInstagramUrl, setAuthorInstagramUrl] = useState(
    (initialData?.author && typeof initialData.author === 'object' ? initialData.author.instagram_url : initialData?.author_instagram_url) || ''
  );
  
  // Available categories for selection
  const categories = ['Travel', 'Events', 'Culture', 'Food', 'Travel Tips', 'Nightlife', 'Accommodation', 'Community'];
  
  // When initialData changes, populate state and convert text blocks to EditorState
  useEffect(() => {
    if (!initialData) return;
    setTitle(initialData.title || '');
    setExcerpt(initialData.excerpt || '');
    setFeaturedImage(initialData.image || '');
    setCategory(initialData.category || 'Travel');
    setTags(Array.isArray(initialData.tags) ? initialData.tags.join(', ') : (initialData.tags || ''));
    setCountryId(initialData.country_id || '');
    setStatus(initialData.status || 'draft');
    // Populate author if present
    if (initialData.author && typeof initialData.author === 'object') {
      setAuthorName(initialData.author.name || '');
      setAuthorSubtitle(initialData.author.subtitle || '');
      setAuthorImage(initialData.author.image || '');
      setAuthorLocationFrom(initialData.author.location_from || '');
      setAuthorLocationCurrent(initialData.author.location_current || '');
      setAuthorTravellerType(initialData.author.traveller_type || '');
      setAuthorTwitterUrl(initialData.author.twitter_url || '');
      setAuthorInstagramUrl(initialData.author.instagram_url || '');
    } else {
      setAuthorName(initialData.author || initialData.author_name || '');
      setAuthorSubtitle(initialData.author_subtitle || '');
      setAuthorImage(initialData.author_image || '');
      setAuthorLocationFrom(initialData.author_location_from || '');
      setAuthorLocationCurrent(initialData.author_location_current || '');
      setAuthorTravellerType(initialData.author_traveller_type || '');
      setAuthorTwitterUrl(initialData.author_twitter_url || '');
      setAuthorInstagramUrl(initialData.author_instagram_url || '');
    }
    const mappedBlocks = Array.isArray(initialData.blocks) ? initialData.blocks.map(b => {
      if (b.type === 'text') {
        try {
          if (typeof b.content === 'string') {
            if (b.content.startsWith('{')) {
              return { ...b, content: draftToHtml(b.content) };
            }
            return { ...b, content: b.content || '<p></p>' };
          }
          if (typeof b.content === 'object' && b.content) {
            return { ...b, content: draftToHtml(JSON.stringify(b.content)) };
          }
          return { ...b, content: '<p></p>' };
        } catch (e) {
          return { ...b, content: '<p></p>' };
        }
      }
      return b;
    }) : [];
    if (mappedBlocks.length) setBlocks(mappedBlocks);
  }, [initialData]);
  
  // Fetch countries for dropdown from API
  useEffect(() => {
    const controller = new AbortController();
    const fetchCountries = async () => {
      axiosPost(
        'countries/list',
        {},
        {},
        (data) => {
          // Expecting an array of { id, name }
          setCountries(Array.isArray(data) ? data : (data?.data || []));
        },
        controller
      );
    };
    fetchCountries();
    return () => controller.abort();
  }, []);
  
  // Function to add a new block
  const addBlock = (type) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      content: type === 'text' 
        ? '<p></p>' 
        : type === 'image'
          ? { images: [] }
          : { url: '', caption: '' }
    };
    
    setBlocks([...blocks, newBlock]);
  };
  
  // Function to move a block (drag and drop)
  const moveBlock = (fromIndex, toIndex) => {
    const updatedBlocks = [...blocks];
    const [movedBlock] = updatedBlocks.splice(fromIndex, 1);
    updatedBlocks.splice(toIndex, 0, movedBlock);
    setBlocks(updatedBlocks);
  };
  
  // Function to remove a block
  const removeBlock = (id) => {
    setBlocks(blocks.filter(block => block.id !== id));
  };
  
  // Function to update block content
  const updateBlockContent = (id, newContent) => {
    setBlocks(blocks.map(block => 
      block.id === id ? { ...block, content: newContent } : block
    ));
  };
  
  // Toggle preview mode
  const togglePreview = () => {
    setIsPreviewMode(!isPreviewMode);
  };
  
  // Prepare preview data
  const getPreviewData = () => {
    // Process blocks for preview
    const processedBlocks = blocks.map(block => {
      if (block.type === 'text') {
        return {
          ...block,
          // Keep HTML content for preview
          content: block.content
        };
      }
      return block;
    });
    
    // Convert tags string to array
    const tagsArray = tags.split(',').map(tag => tag.trim()).filter(tag => tag);
    
    // Prepare data for preview
    return {
      title,
      excerpt,
      image: featuredImage,
      category,
      country_id: countryId,
      tags: tagsArray,
      blocks: processedBlocks,
      date_published: initialData?.date_published || Math.floor(Date.now() / 1000),
      author: (authorName || authorSubtitle || authorImage || authorLocationFrom || authorLocationCurrent || authorTravellerType || authorTwitterUrl || authorInstagramUrl) ? { name: authorName, subtitle: authorSubtitle, image: authorImage, location_from: authorLocationFrom, location_current: authorLocationCurrent, traveller_type: authorTravellerType, twitter_url: authorTwitterUrl, instagram_url: authorInstagramUrl } : (initialData?.author || ''),
    };
  };
  
  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Skip country validation for site pages
    if (!isSitePage && !isTravelGuide && !countryId) {
      alert('Please select a country for this article.');
      return;
    }
    
    // Process blocks for saving - keep HTML for queer-landscape, convert to Draft.js for articles
    const processedBlocks = blocks.map(block => {
      if (block.type === 'text') {
        return {
          ...block,
          content: useParentSave ? block.content : htmlToDraft(block.content)
        };
      }
      return block;
    });
    
    // Convert tags string to array
    const tagsArray = tags.split(',').map(tag => tag.trim()).filter(tag => tag);
    
    // Prepare data for saving
    const articleData = {
      id: initialData?.id, // include id if editing
      title,
      excerpt,
      image: featuredImage,
      category,
      country_id: countryId,
      tags: tagsArray,
      blocks: processedBlocks,
      date_published: initialData?.date_published || Math.floor(Date.now() / 1000),
      status: status,
      author: (authorName || authorSubtitle || authorImage || authorLocationFrom || authorLocationCurrent || authorTravellerType || authorTwitterUrl || authorInstagramUrl) ? { name: authorName, subtitle: authorSubtitle, image: authorImage, location_from: authorLocationFrom, location_current: authorLocationCurrent, traveller_type: authorTravellerType, twitter_url: authorTwitterUrl, instagram_url: authorInstagramUrl } : (initialData?.author || ''),
    };
    
    // For queer-landscape or site-page mode, just call onSave callback and let parent handle API
    if (useParentSave) {
      if (typeof onSave === 'function') {
        onSave(articleData);
      }
      return;
    }
    
    // Submit to API via axios helper (for regular articles)
    const endpoint = initialData?.id ? 'articles/update' : 'articles/create';
    axiosPost(
      endpoint,
      {},
      articleData,
      (data) => {
        const savedId = data?.data?.id || data?.id || articleData?.id;
        const doFinish = () => {
          if (typeof onSave === 'function') onSave(data || articleData);
          alert('Article saved successfully.');
        };
        // Upsert author details into articles_authors (best-effort)
        if (savedId && (authorName || authorSubtitle || authorImage || authorLocationFrom || authorLocationCurrent || authorTravellerType || authorTwitterUrl || authorInstagramUrl)) {
          axiosPost(
            'articles/authors/upsert',
            { 'Content-Type': 'application/json' },
            { article_id: savedId, name: authorName, subtitle: authorSubtitle, image: authorImage, location_from: authorLocationFrom, location_current: authorLocationCurrent, traveller_type: authorTravellerType, twitter_url: authorTwitterUrl, instagram_url: authorInstagramUrl },
            () => doFinish()
          );
        } else {
          doFinish();
        }
      }
    );
  };
  
  return (
    <DndProvider backend={HTML5Backend}>
      <div className="article-editor">
        <div className="editor-header">
          <h2 className="editor-title">
            {isQueerLandscape 
              ? (initialData?.id ? 'Edit Queer Landscape' : 'Create Queer Landscape')
              : isTravelGuide
                ? (initialData?.id ? 'Edit Travel Guide' : 'Create Travel Guide')
              : isSitePage
                ? (initialData?.id ? 'Edit Site Page' : 'Create Site Page')
                : (initialData?.id ? 'Edit Article' : 'Create New Article')
            }
          </h2>
          <button 
            type="button" 
            className={`preview-toggle-button ${isPreviewMode ? 'active' : ''}`}
            onClick={togglePreview}
          >
            {isPreviewMode ? (
              <>
                <FaEdit /> Edit Mode
              </>
            ) : (
              <>
                <FaEye /> Preview
              </>
            )}
          </button>
        </div>
        
        {isPreviewMode ? (
          <div className="editor-preview-surface">
            <ArticlePreview articleData={getPreviewData()} />
          </div>
        ) : (
        
        <form onSubmit={handleSubmit}>
          {/* Basic Article Information */}
          <div className="editor-section">
            <h3 className="section-title">Basic Information</h3>
            
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter article title"
                required
                style={{ background: '#2a2a2a' }}
              />
            </div>
            
            <div className="form-group">
              <label>Excerpt</label>
              <textarea
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                placeholder="Brief summary of your article"
                required
                rows="2"
              />
            </div>
            
            <div className="form-group">
              <label>Featured Image</label>
              <div className="featured-image-input">
                <input
                  type="text"
                  value={featuredImage}
                  onChange={(e) => setFeaturedImage(e.target.value)}
                  placeholder="Image URL"
                />
                <label className="upload-button">
                  Upload
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setFeaturedImage(event.target.result);
                        };
                        reader.readAsDataURL(e.target.files[0]);
                      }
                    }}
                  />
                </label>
              </div>
              {featuredImage && (
                <div className="featured-image-preview">
                  <img src={featuredImage} alt="Featured" />
                  <button 
                    type="button" 
                    className="remove-image" 
                    onClick={() => setFeaturedImage('')}
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
            </div>
            
            <div className="form-row">
              {!isSitePage && (
                <div className="form-group">
                  <label>Country</label>
                  <select
                    value={countryId}
                    onChange={(e) => setCountryId(e.target.value)}
                    required
                  >
                    <option value="" disabled>Select a country</option>
                    {countries.map((c) => (
                      <option key={c.id} value={c.id}>{c.name || c.title}</option>
                    ))}
                  </select>
                </div>
              )}
              {!isSitePage && !isTravelGuide && (
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {!isSitePage && !isTravelGuide && (
                <div className="form-group">
                  <label>Tags (comma separated)</label>
                  <input
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="travel, beaches, lgbtq-friendly"
                  />
                </div>
              )}
              {!isSitePage && !isTravelGuide && (
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Author Information - hide for site pages and travel guides */}
          {!isSitePage && !isTravelGuide && (
          <div className="editor-section">
            <h3 className="section-title">Author</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Author Name</label>
                <input
                  type="text"
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  placeholder="e.g., Zack Cahill"
                />
              </div>
              <div className="form-group">
                <label>Author Role/Subtitle</label>
                <input
                  type="text"
                  value={authorSubtitle}
                  onChange={(e) => setAuthorSubtitle(e.target.value)}
                  placeholder="e.g., EDITOR AT LARGE"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Location From</label>
                <input
                  type="text"
                  value={authorLocationFrom}
                  onChange={(e) => setAuthorLocationFrom(e.target.value)}
                  placeholder="e.g., London, UK"
                />
              </div>
              <div className="form-group">
                <label>Current Location</label>
                <input
                  type="text"
                  value={authorLocationCurrent}
                  onChange={(e) => setAuthorLocationCurrent(e.target.value)}
                  placeholder="e.g., Dublin, Ireland"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Traveller Type</label>
                <input
                  type="text"
                  value={authorTravellerType}
                  onChange={(e) => setAuthorTravellerType(e.target.value)}
                  placeholder="e.g., Adventurer"
                />
              </div>
              <div className="form-group">
                <label>Twitter URL</label>
                <input
                  type="text"
                  value={authorTwitterUrl}
                  onChange={(e) => setAuthorTwitterUrl(e.target.value)}
                  placeholder="https://twitter.com/username"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Instagram URL</label>
                <input
                  type="text"
                  value={authorInstagramUrl}
                  onChange={(e) => setAuthorInstagramUrl(e.target.value)}
                  placeholder="https://instagram.com/username"
                />
              </div>
              <div className="form-group">
                <label>Author Image</label>
                <div className="featured-image-input">
                  <input
                    type="text"
                    value={authorImage}
                    onChange={(e) => setAuthorImage(e.target.value)}
                    placeholder="Image URL"
                  />
                  <label className="upload-button">
                    Upload
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            setAuthorImage(event.target.result);
                          };
                          reader.readAsDataURL(e.target.files[0]);
                        }
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>
            {authorImage && (
              <div className="featured-image-preview">
                <img src={authorImage} alt="Author" />
                <button
                  type="button"
                  className="remove-image"
                  onClick={() => setAuthorImage('')}
                >
                  <FaTimes />
                </button>
              </div>
            )}
          </div>
          )}
          
          {/* Content Blocks */}
          <div className="editor-section">
            <h3 className="section-title">Content</h3>
            <p className="section-description">
              Drag and drop blocks to rearrange your article content.
            </p>
            
            <div className="content-blocks">
              {blocks.map((block, index) => (
                <ContentBlock
                  key={block.id}
                  id={block.id}
                  type={block.type}
                  content={block.content}
                  index={index}
                  moveBlock={moveBlock}
                  removeBlock={removeBlock}
                  updateBlockContent={updateBlockContent}
                />
              ))}
            </div>
            
            <div className="add-block-buttons">
              <button 
                type="button" 
                className="add-block-button text"
                onClick={() => addBlock('text')}
              >
                Add Text Block
              </button>
              <button 
                type="button" 
                className="add-block-button image"
                onClick={() => addBlock('image')}
              >
                <FaImage /> Add Image
              </button>
              <button 
                type="button" 
                className="add-block-button video"
                onClick={() => addBlock('video')}
              >
                <FaVideo /> Add Video
              </button>
            </div>
          </div>
          
          {/* Submit Buttons */}
          <div className="editor-actions">
            <button type="button" className="cancel-button" onClick={() => onClose ? onClose() : window.history.back()}>
              Cancel
            </button>
            <button type="submit" className="save-button">
              {isQueerLandscape 
                ? (initialData?.id ? 'Update Queer Landscape' : 'Save Queer Landscape')
                : isTravelGuide
                  ? (initialData?.id ? 'Update Travel Guide' : 'Save Travel Guide')
                : isSitePage
                  ? (initialData?.id ? 'Update Page' : 'Save Page')
                  : (initialData?.id ? 'Update Article' : 'Publish Article')
              }
            </button>
          </div>
        </form>
        )}
      </div>
    </DndProvider>
  );
};

export default ArticleEditor;
